import { useState, useEffect } from "react";
import { getData, putDataToken } from "../axios/axiosHelper";
import toast from "react-hot-toast";
import { ADMIN_PAGE_LABELS } from "../navigation/adminNavigation";

const DEFAULT_ANDROID_POLICY = {
  latestVersionCode: 10,
  latestVersionName: "1.1.0",
  minimumSupportedVersionCode: 1,
  optionalUpdateEnabled: false,
  optionalPromptMode: "ONCE_PER_VERSION",
  playStoreUrl:
    "https://play.google.com/store/apps/details?id=com.neon.installmentWorld",
  forceUpdateTitle: {
    en: "Update required",
    ar: "التحديث مطلوب",
  },
  forceUpdateMessage: {
    en: "Please update the app to continue.",
    ar: "يرجى تحديث التطبيق للمتابعة.",
  },
  optionalUpdateTitle: {
    en: "Update available",
    ar: "يتوفر تحديث جديد",
  },
  optionalUpdateMessage: {
    en: "A newer version is available. You can update now or continue.",
    ar: "يتوفر إصدار أحدث. يمكنك التحديث الآن أو المتابعة.",
  },
};

const OPTIONAL_PROMPT_MODES = [
  "ONCE_PER_VERSION",
  "DAILY",
  "EVERY_LAUNCH",
];

const mergeAndroidPolicy = (policy) => ({
  ...DEFAULT_ANDROID_POLICY,
  ...(policy || {}),
  forceUpdateTitle: {
    ...DEFAULT_ANDROID_POLICY.forceUpdateTitle,
    ...(policy?.forceUpdateTitle || {}),
  },
  forceUpdateMessage: {
    ...DEFAULT_ANDROID_POLICY.forceUpdateMessage,
    ...(policy?.forceUpdateMessage || {}),
  },
  optionalUpdateTitle: {
    ...DEFAULT_ANDROID_POLICY.optionalUpdateTitle,
    ...(policy?.optionalUpdateTitle || {}),
  },
  optionalUpdateMessage: {
    ...DEFAULT_ANDROID_POLICY.optionalUpdateMessage,
    ...(policy?.optionalUpdateMessage || {}),
  },
});

const GlobalSettingsHook = () => {
  const NavItems = [
    {
      name: ADMIN_PAGE_LABELS.globalSettings,
      to: "/globalSettings",
      // icon: CountriesCities,
    },
  ];
  const [loading, setLoading] = useState(false);

  const [users, setUsers] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [platformFeePercentage, setPlatformFeePercentage] = useState(null);
  const [gracePeriod, setgracePeriod] = useState(null);
  const [delayDayFees, setdelayDayFees] = useState(null);
  const [androidPolicy, setAndroidPolicy] = useState(DEFAULT_ANDROID_POLICY);
  const [settingsSaveLoading, setSettingsSaveLoading] = useState(false);
  const [appUpdateSaveLoading, setAppUpdateSaveLoading] = useState(false);

  const [show, setShow] = useState(false);
  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  const handleEditGobalSettings = async (data) => {
    setPlatformFeePercentage(
      data?.platformFeePolicy?.feePercentage ?? data.interestRate
    );
    setgracePeriod(data.gracePeriod);
    setdelayDayFees(data.delayDayFees);
    setAndroidPolicy(
      mergeAndroidPolicy(data?.appUpdatePolicy?.android)
    );

    setShowModal(true);
  };
  const getUsers = () => {
    setLoading(true);
    getData(`/global`, true)
      .then((response) => {
        const data = response?.data?.data || {};
        setUsers({
          ...data,
          appUpdatePolicy: {
            android: mergeAndroidPolicy(data?.appUpdatePolicy?.android),
          },
        });
        setLoading(false);

      })
      .catch((err) => {
        toast.error(err?.response?.data?.message);
        setLoading(false);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    getUsers();
  }, []);

  const updateAndroidPolicy = (field, value) => {
    setAndroidPolicy((previous) => ({
      ...previous,
      [field]: value,
    }));
  };

  const updateAndroidPolicyText = (field, lang, value) => {
    setAndroidPolicy((previous) => ({
      ...previous,
      [field]: {
        ...previous[field],
        [lang]: value,
      },
    }));
  };

  const validatePlatformSettings = () => {
    const parsedPlatformFeePercentage = Number(platformFeePercentage);
    if (
      !Number.isFinite(parsedPlatformFeePercentage) ||
      parsedPlatformFeePercentage <= 0
    ) {
      toast.error("Platform fees percentage must be greater than 0");
      return false;
    }
    if (!Number.isFinite(Number(gracePeriod)) || Number(gracePeriod) < 0) {
      toast.error("Legacy grace period must be 0 or greater");
      return false;
    }
    if (!Number.isFinite(Number(delayDayFees)) || Number(delayDayFees) < 0) {
      toast.error("Legacy delay day fees must be 0 or greater");
      return false;
    }
    return true;
  };

  const validateAndroidPolicy = () => {
    const latestVersionCode = Number(androidPolicy.latestVersionCode);
    const minimumSupportedVersionCode = Number(
      androidPolicy.minimumSupportedVersionCode
    );

    if (!Number.isInteger(latestVersionCode) || latestVersionCode < 0) {
      toast.error("Latest version code must be a non-negative integer");
      return false;
    }
    if (
      !Number.isInteger(minimumSupportedVersionCode) ||
      minimumSupportedVersionCode < 0
    ) {
      toast.error("Minimum supported version code must be a non-negative integer");
      return false;
    }
    if (minimumSupportedVersionCode > latestVersionCode) {
      toast.error(
        "Minimum supported version code must be less than or equal to latest version code"
      );
      return false;
    }
    if (!OPTIONAL_PROMPT_MODES.includes(androidPolicy.optionalPromptMode)) {
      toast.error("Optional prompt mode is invalid");
      return false;
    }
    try {
      const url = new URL(androidPolicy.playStoreUrl);
      if (url.protocol !== "https:") {
        toast.error("Play Store URL must use HTTPS");
        return false;
      }
    } catch (error) {
      toast.error("Play Store URL must be valid");
      return false;
    }
    return true;
  };

  const buildAndroidPolicyPayload = () => ({
    latestVersionCode: Number(androidPolicy.latestVersionCode),
    latestVersionName: androidPolicy.latestVersionName,
    minimumSupportedVersionCode: Number(
      androidPolicy.minimumSupportedVersionCode
    ),
    optionalUpdateEnabled: Boolean(androidPolicy.optionalUpdateEnabled),
    optionalPromptMode: androidPolicy.optionalPromptMode,
    playStoreUrl: androidPolicy.playStoreUrl,
    forceUpdateTitle: androidPolicy.forceUpdateTitle,
    forceUpdateMessage: androidPolicy.forceUpdateMessage,
    optionalUpdateTitle: androidPolicy.optionalUpdateTitle,
    optionalUpdateMessage: androidPolicy.optionalUpdateMessage,
  });

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!validatePlatformSettings()) return;
    setSettingsSaveLoading(true);

    putDataToken(
      "/global",
      {
        gracePeriod: gracePeriod,
        delayDayFees: delayDayFees,
        platformFeePolicy: {
          ...(users?.platformFeePolicy || {}),
          feePercentage: Number(platformFeePercentage),
          feeBaseType: "PRODUCT_CASH_PRICE",
        }
      },
      true
    )
      .then((response) => {
        getUsers();
        setShowModal(false);
        toast.success(response?.data?.message);
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message);
      })
      .finally(() => setSettingsSaveLoading(false));
  };

  const handleAppUpdatePolicySave = async (e) => {
    e.preventDefault();
    if (!validateAndroidPolicy()) return;
    setAppUpdateSaveLoading(true);

    putDataToken(
      "/global",
      {
        appUpdatePolicy: {
          android: buildAndroidPolicyPayload(),
        },
      },
      true
    )
      .then((response) => {
        getUsers();
        toast.success(response?.data?.message || "App update policy saved");
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message);
      })
      .finally(() => setAppUpdateSaveLoading(false));
  };

  return {
    NavItems,
    handleShow,
    show,
    handleClose,
    users,
    loading,
    settingsSaveLoading,
    appUpdateSaveLoading,
    handleUpdate,
    handleAppUpdatePolicySave,
    showModal,
    setShowModal,
    setPlatformFeePercentage,
    setgracePeriod,
    setdelayDayFees,
    gracePeriod,
    platformFeePercentage,
    delayDayFees,
    handleEditGobalSettings,
    androidPolicy,
    optionalPromptModes: OPTIONAL_PROMPT_MODES,
    updateAndroidPolicy,
    updateAndroidPolicyText,
  };
};

export default GlobalSettingsHook;
